/**
 * ゲーム画面上のオーバーレイ（開始前の操作説明 / 一時停止 / ゲームオーバー）。
 * ゲーム状態に応じて自動的に切り替わる。
 */

import { GameState, type GameStateValue } from '../core/GameState';
import type { GameContext } from '../game/GameContext';
import { stageIdOf } from '../game/config/stages';
import type { DailyBook } from '../meta/daily';
import {
  describeDailyRank,
  fetchDailyBoard,
  sampleBoardForMock,
  submitDailyScore,
  type RankingIdentityStore,
} from '../meta/dailyRanking';
import type { RankingModal } from './RankingModal';
import { highlightBanner, shareHighlight, type HighlightClip } from './HighlightRecorder';
import type { TrophyBook } from '../meta/progress';
import { shouldOfferContinue } from '../meta/assist';
import { TIER_LABEL, medalOf, nextMedalGoal, type PrefMedal, type TrophyStatus } from '../meta/trophies';
import { dailyBestHtml, highScoreMarkHtml } from './recordLabels';
import type { AppPrompts } from '../meta/appPrompts';
import { confirmDialog } from './confirmDialog';
import { ScoreHistory } from './ScoreHistory';
import { applyTextSize, isLargeText, isTouchDevice } from './inputMode';
import { ShareButtons } from './ShareButtons';
import type { Moment } from './MomentCapture';
import { MomentPicker } from './MomentPicker';
import type { ShareVisual } from './ShareCard';
import { hashtags } from './ShareSystem';
import { publicHost } from '../platform/native';
import { track } from '../meta/telemetry';
import type { ShareableResult } from './ShareSystem';

export interface OverlayOptions {
  /** ゲームオーバーのたびにトロフィーを判定し、新しく取れたものを出す。 */
  trophies?: TrophyBook;
  /** 「トロフィー棚を見る」を押したとき。 */
  onOpenTrophies?: () => void;
  /** トロフィーを判定し直したあと（新着バッジの更新用）。 */
  onTrophiesSynced?: () => void;
  /** 「ショップ」を開く（アプリ版のみ）。 */
  onOpenShop?: () => void;
  /** 今日の県で遊んでいるとき、その記録。 */
  daily?: DailyBook;
  /** 今日の県のランキング（サーバーが未設定なら「準備中」と出るだけ）。 */
  ranking?: { ids: RankingIdentityStore; modal: RankingModal };
  /** このプレイの名場面（録画が有効なときだけ）。 */
  getHighlight?: () => HighlightClip | null;
  /** このプレイの名場面の静止画（最高Lv・最大コンボ・最後の盤面）。共有カードに載せる。 */
  getMoments?: () => Moment[];
  /** レビュー依頼・今日の県のお知らせ（iOS アプリだけ動く）。 */
  appPrompts?: AppPrompts;
}

interface OverlayAction {
  label: string;
  ariaLabel?: string;
  primary?: boolean;
  /** 小さく下に並べる（結果画面のボタンを 3 つまでに絞るため）。 */
  sub?: boolean;
  onClick: () => void;
}


export class OverlayController {
  private readonly context: GameContext;
  private readonly root: HTMLElement | null;
  private readonly titleEl: HTMLElement | null;
  private readonly bodyEl: HTMLElement | null;
  private readonly actionsEl: HTMLElement | null;
  private readonly shareEl: HTMLElement | null;
  private readonly cardEl: HTMLElement | null;
  private readonly scoreHistory: ScoreHistory;
  private lastResult: {
    score: number;
    highScore: number;
    isNewRecord: boolean;
    maxLevelReached: number;
    assisted: boolean;
    relaxed: boolean;
    drops?: number;
    playMs?: number;
    spilled?: { name: string; level: number; side: 'left' | 'right' };
    maxCombo?: number;
  } | null = null;
  /** いまのんびりモードか。 */
  private relaxed = false;
  private readonly options: OverlayOptions;
  /** いま映っている景色（共有カードの背景に使う）。 */
  private sceneId: string | null = null;
  /** ゲームオーバー画面を出している最中の広告待ち（二度押し防止）。 */
  private busy = false;
  /** 「名場面を保存しますか？」を聞いた名場面（同じものには二度聞かない）。 */
  private askedHighlightClip: HighlightClip | null = null;
  /** このプレイを始めたときの県メダル（レビュー依頼の「初めて取った」の判定用）。 */
  private medalAtStart: PrefMedal = 'none';
  /** 今日の県を遊んだ日数（このゲームオーバーで記録したあと）。今日の県でなければ 0。 */
  private dailyDaysPlayed = 0;
  /** いま結果画面に出している共有の中身（順位があとから届いたら書き足す）。 */
  private shareable: ShareableResult | null = null;
  /** 結果画面を出した回数。前のプレイの順位が、次の結果画面に紛れ込まないように使う。 */
  private resultSerial = 0;
  /** 「操作説明」ボタンで一時停止したか（一時停止の画面で遊び方を開いて出す）。 */
  private helpRequested = false;
  /** ゲームオーバーを読み上げで知らせる場所（画面には出さない）。 */
  private readonly announcer: HTMLElement;

  constructor(context: GameContext, root: ParentNode = document, options: OverlayOptions = {}) {
    this.context = context;
    this.options = options;
    this.root = root.querySelector<HTMLElement>('[data-ui="overlay"]');
    this.titleEl = root.querySelector<HTMLElement>('[data-ui="overlay-title"]');
    this.bodyEl = root.querySelector<HTMLElement>('[data-ui="overlay-body"]');
    this.actionsEl = root.querySelector<HTMLElement>('[data-ui="overlay-actions"]');
    this.shareEl = root.querySelector<HTMLElement>('[data-ui="overlay-share"]');
    // 結果画面が枠に収まらないとき「下につづきます」を出す（iPhone SE などで共有ボタンが見切れていた）
    this.cardEl = this.root?.querySelector<HTMLElement>('.stage-overlay__card') ?? null;
    this.cardEl?.addEventListener('scroll', () => this.updateMoreHint(), { passive: true });
    this.shareEl?.addEventListener('toggle', () => this.updateMoreHint(), true);
    this.scoreHistory = new ScoreHistory(stageIdOf(context.stage));
    // 結果の枠は表示を切り替えるたびに隠れるので、読み上げ用の場所は枠の外に置く
    this.announcer = document.createElement('div');
    this.announcer.className = 'sr-only';
    this.announcer.setAttribute('role', 'status');
    this.announcer.setAttribute('aria-live', 'polite');
    document.body.append(this.announcer);

    context.state.onChange((to) => this.applyState(to));
    context.bus.on('mode:change', ({ relaxed }) => {
      this.relaxed = relaxed;
    });
    context.bus.on('tour:scene', ({ scene }) => {
      this.sceneId = scene?.id ?? null;
    });
    this.medalAtStart = this.currentMedal();
    const resetMedal = () => {
      this.medalAtStart = this.currentMedal();
    };
    context.bus.on('command:start', resetMedal);
    context.bus.on('command:restart', resetMedal);
    context.bus.on('game:over', (payload) => {
      this.lastResult = payload;
      this.dailyDaysPlayed = 0;
      this.showGameOver();
      this.afterResult();
    });
    root.querySelector<HTMLButtonElement>('[data-ui="help"]')?.addEventListener('click', () => {
      this.showHelp();
    });

    this.applyState(context.state.state);
  }

  private applyState(state: GameStateValue): void {
    switch (state) {
      case GameState.READY:
        this.showReady();
        break;
      case GameState.PAUSED:
        this.showPaused();
        break;
      case GameState.GAME_OVER:
        // 状態の変化だけでは結果がまだ届いていない。'game:over' の通知を待って出す
        break;
      default:
        this.hide();
    }
  }

  /** 遊び方の箇条書き（開始画面と、一時停止の画面で同じものを出す）。 */
  private howToHtml(): string {
    // スマホではキー操作の説明を出さない（出ていると「何を押すの？」になる）
    return isTouchDevice()
      ? `
          <li>指を左右に動かして、はなすと料理が落ちる</li>
          <li>同じ料理どうしがぶつかると 1段階上へ進化</li>
          <li>器からこぼれて外に落ちるとゲームオーバー</li>
          <li>器の口より高く積んでも、こぼさなければセーフ</li>`
      : `
          <li>マウスを左右に動かして落下位置を決め、クリックで落とす</li>
          <li>← → キーでも移動、スペース / Enter で落とす</li>
          <li>同じ料理どうしがぶつかると 1段階上へ進化</li>
          <li>P キーで一時停止、R キーでリスタート</li>
          <li>器からこぼれて外に落ちるとゲームオーバー</li>
          <li>器の口より高く積んでも、こぼさなければセーフ</li>`;
  }

  private showReady(): void {
    const stage = this.context.stage;
    const howTo = this.howToHtml();
    const dailyNote = this.context.daily
      ? `<p class="result-daily">今日の県（${this.context.daily.shortDate}）：今日は全員、同じ順番で料理が落ちてきます。スコアを見せ合おう。</p>`
      : '';
    // 2回目からは遊び方をたたんでおく（毎回読む説明ではない）
    const firstTime = this.context.storage.playCount === 0;
    const rules = firstTime
      ? `<ul>${howTo}\n        </ul>`
      : `<details class="rules-fold"><summary>遊び方を見る</summary><ul>${howTo}\n        </ul></details>`;
    this.show({
      title: this.context.daily ? `今日の県：${stage.regionName}` : stage.title,
      modal: true,
      html: `${dailyNote}
        <p>${stage.regionName}の名物めしを合体させて、最上位の一品を目指しましょう。</p>
        ${rules}
      `,
      actions: [
        {
          label: 'はじめる',
          ariaLabel: 'ゲームをはじめる',
          primary: true,
          onClick: () => void this.startGame(false),
        },
        {
          label: '🍵 のんびり（こぼれない）',
          ariaLabel: 'のんびりモードではじめる。こぼれてもゲームオーバーにならない',
          onClick: () => void this.startGame(true),
        },
        {
          label: isLargeText() ? '文字を標準に' : '文字を大きく',
          ariaLabel: isLargeText() ? '文字の大きさを標準に戻す' : '文字を大きくする',
          sub: true,
          onClick: () => {
            const large = !isLargeText();
            applyTextSize(large);
            Object.assign(this.context.settings, this.context.storage.updateSettings({ largeText: large }));
            this.context.bus.emit('settings:change', { settings: this.context.settings });
            this.showReady();
          },
        },
      ],
    });
  }

  private showPaused(): void {
    // 「操作説明」ボタンで止めたときは遊び方を開いて出す。ふつうに止めたときはたたんでおく
    // （押しても「ひと休み中」が出るだけで説明が出なかった。2026-10-05）
    const openRules = this.helpRequested;
    this.helpRequested = false;
    const assistNote = this.context.assist.enabled
      ? '<li>🥢 おはし：ボタンを押してから料理をタップし、「はい」で1つ消せる</li>'
      : '';
    this.show({
      title: this.relaxed ? 'ひと休み中（のんびり）' : 'ひと休み中',
      html: `<p>続きはいつでも。</p>
        <details class="rules-fold"${openRules ? ' open' : ''}><summary>遊び方を見る</summary><ul>${this.howToHtml()}${assistNote}
        </ul></details>`,
      showShare: true,
      actions: [
        {
          label: '再開する',
          primary: true,
          onClick: () => this.context.bus.emit('command:resume'),
        },
        {
          label: 'リスタート',
          onClick: () => this.context.bus.emit('command:requestRestart'),
        },
        ...(this.relaxed
          ? [{ label: 'おわる', onClick: () => this.context.bus.emit('command:finish') }]
          : []),
      ],
    });
  }

  private showGameOver(): void {
    const result = this.lastResult;
    const maxItem = result ? this.context.itemByLevel.get(result.maxLevelReached) : undefined;
    const maxItemName = maxItem ? `レベル${maxItem.level} ${maxItem.name}` : 'なし';

    if (result && !result.relaxed) {
      this.scoreHistory.add({
        score: result.score,
        highScore: result.highScore,
        isNewRecord: result.isNewRecord,
        maxLevelReached: result.maxLevelReached,
        maxItemName,
        stageTitle: this.context.stage.title,
      });
    }

    // 今日の県の記録はトロフィー判定より先に（連続日数のトロフィーがあるため）
    const dailyHtml = this.recordDaily();
    const newTrophies = this.syncTrophies();
    const assist = this.context.assist;
    const actions: OverlayAction[] = [];
    const relaxedRun = result?.relaxed === true;
    if (relaxedRun) {
      actions.push({
        label: 'ふつうモードで',
        onClick: () => {
          this.context.bus.emit('command:setMode', { relaxed: false });
          this.context.bus.emit('command:restart');
        },
      });
    } else if (assist.canContinue() && result && shouldOfferContinue(result.score, result.highScore)) {
      // ごほうび広告をすすめた（見た数 ad_rewarded と並べて opt-in 率を出す）
      track('ad_offer', { detail: 'continue' });
      actions.push({
        label: '▶ 広告を見て続ける（上の3つを消す）',
        // 見た目と読み上げの言葉をそろえる（▶ は「再生」と読まれるので外す）
        ariaLabel: '広告を見て続ける（上の3つを消す）',
        onClick: () => void this.continueByAd(),
      });
    }
    // 「もう一度」は常にいちばん目立たせる（2プレイ目に進んでもらうのが最優先）
    actions.unshift({
      label: 'もう一度あそぶ',
      primary: true,
      onClick: () => void this.playAgain(),
    });
    if (this.context.daily && this.options.ranking && !relaxedRun) {
      const daily = this.context.daily;
      const modal = this.options.ranking.modal;
      actions.push({
        label: '📊 ランキング',
        ariaLabel: '今日のランキングを見る',
        // 小さい画面で結果画面が枠からはみ出さないよう、棚・ショップと同じ小さいボタンの行に並べる
        sub: true,
        onClick: () =>
          // 送信が先に届くよう、少しだけ待ってから開く
          window.setTimeout(
            () =>
              void modal.open({
                date: daily.dateKey,
                region: this.context.stage.regionId,
                regionName: this.context.stage.regionName,
                shortDate: daily.shortDate,
              }),
            300,
          ),
      });
    }
    if (newTrophies.length > 0 && this.options.onOpenTrophies) {
      actions.push({ label: '🏆 棚を見る', sub: true, onClick: () => this.options.onOpenTrophies?.() });
    }
    const clip = this.options.getHighlight?.();
    if (clip) {
      actions.push({
        label: `🎬 名場面（Lv${clip.level}）`,
        sub: true,
        ariaLabel: `Lv${clip.level}ができた瞬間の動画を共有・保存する`,
        onClick: () => void shareHighlight(clip, this.highlightFileBase(clip)),
      });
    }
    if (assist.enabled && this.options.onOpenShop) {
      actions.push({
        label: '🥢 ショップ',
        ariaLabel: 'ショップを開く（おはし・買い切り）',
        sub: true,
        onClick: () => this.options.onOpenShop?.(),
      });
    }

    const title = relaxedRun ? 'おつかれさま' : result?.isNewRecord ? 'ハイスコア更新！' : 'ゲームオーバー';
    this.announce(`${title}。スコア ${(result?.score ?? 0).toLocaleString('ja-JP')}点`);
    this.show({
      title,
      modal: true,
      html:
        this.buildResultHtml(result, maxItemName, result?.maxCombo) +
        this.buildSpillNote() +
        (relaxedRun ? '' : this.buildNextGoal()) +
        (relaxedRun
          ? '<p class="result-note">🍵 のんびりモードでした。図鑑は埋まりますが、ハイスコア・メダル・今日の県の記録には入りません。</p>'
          : '') +
        dailyHtml +
        this.buildAssistNote() +
        this.buildTrophyHtml(newTrophies),
      showShare: true,
      moments: this.options.getMoments?.() ?? [],
      actions,
    });
  }

  private recordDaily(): string {
    const book = this.options.daily;
    const daily = this.context.daily;
    const serial = ++this.resultSerial;
    if (!book || !daily || !this.lastResult || this.lastResult.relaxed) return '';
    book.record(this.lastResult.score, undefined, { assisted: this.lastResult.assisted });
    const view = book.view();
    // ランキングには、コンティニューもおはしも使わなかったプレイだけ送る（同じ条件で比べるため）
    const ranking = this.options.ranking;
    let rankNote = '';
    if (ranking) {
      if (this.lastResult.assisted) {
        rankNote = '<br><small>コンティニュー・おはしを使ったプレイはランキングに入りません。</small>';
      } else if (this.lastResult.maxLevelReached >= 1) {
        const identity = ranking.ids.identity;
        const region = this.context.stage.regionId;
        const score = this.lastResult.score;
        const mock = this.context.assist.platform.kind === 'mock';
        void submitDailyScore(identity, {
          date: daily.dateKey,
          region,
          score: this.lastResult.score,
          maxLevel: this.lastResult.maxLevelReached,
          drops: this.lastResult.drops ?? 0,
          playMs: this.lastResult.playMs ?? 0,
        })
          .then(() => fetchDailyBoard(identity, daily.dateKey, region))
          // テスト用のモック（localhost・staging の ?platform=mock）だけ、サーバーが無くても見本の順位で見た目を確かめる
          .then((board) => (board.status === 'ok' || !mock ? board : sampleBoardForMock(score)))
          .then((board) => {
            if (serial !== this.resultSerial) return;
            const el = this.bodyEl?.querySelector<HTMLElement>('[data-ui="daily-rank"]');
            if (!el) return;
            const text = board.status === 'ok' ? describeDailyRank(board) : null;
            // 取れないとき（オフライン・サーバー未設定）は何も出さない
            if (!text || board.status !== 'ok' || !board.me) {
              el.remove();
              return;
            }
            el.textContent = `📊 ${text}${board.sample ? '（見本）' : ''}`;
            // 共有の文面にも「○人中○位」を入れる（ボタンは同じ中身を見ている）
            if (this.shareable) this.shareable.dailyRank = { rank: board.me.rank, total: board.total };
          });
        rankNote = '<br><span class="result-rank" data-ui="daily-rank">📊 順位を確かめています…</span>';
      }
    }
    this.dailyDaysPlayed = view.totalDays;
    // 連続日数のごほうび：7日ごとにおはし1本（アプリ版・モックだけ。Web版はおはしが無い）
    let streakNote = '';
    if (this.context.assist.enabled && book.claimStreakReward()) {
      this.context.assist.giftItems(1);
      track('streak_reward', { detail: String(view.streak) });
      streakNote = `<p class="result-streak-reward" role="status">🥢 ${view.streak}日連続！おはしを1本どうぞ</p>`;
    }
    return `<p class="result-daily">今日の県 ${daily.shortDate} ・ ${dailyBestHtml(view)} ・ 連続 <b>${view.streak}</b>日${rankNote}</p>${streakNote}`;
  }

  private buildShareVisual(): ShareVisual {
    const { stage, items, icons, tourPhotos } = this.context;
    return {
      regionName: stage.regionName,
      items: items.map((item) => ({
        level: item.level,
        name: item.name,
        iconUrl: icons.iconUrl(item, 256),
      })),
      reachedLevel: this.lastResult?.maxLevelReached ?? 0,
      sceneUrl: (this.sceneId && tourPhotos.get(this.sceneId)) || undefined,
      host: publicHost(),
      hashtags: hashtags(stage.title),
      dailyLabel: this.context.daily ? `今日の県 ${this.context.daily.shortDate}` : undefined,
      maxCombo: this.lastResult?.maxCombo,
    };
  }

  private syncTrophies(): TrophyStatus[] {
    if (!this.options.trophies) return [];
    const snapshot = this.options.trophies.sync();
    this.options.onTrophiesSynced?.();
    return snapshot.newlyUnlocked;
  }

  /** この県のいまのメダル。 */
  private currentMedal(): PrefMedal {
    const storage = this.context.storage;
    return medalOf({
      maxLevel: this.context.maxLevel,
      reached: storage.maxLevelReached,
      reachedNoAssist: storage.bestLevelNoAssist,
    });
  }

  /**
   * 結果画面のあとの「お願い」（iOS アプリだけ。1回のゲームオーバーで1つまで）。
   * 1. 今日の県を2日目に遊び終えた → 毎朝のお知らせを聞く（1回だけ）
   * 2. 県メダル（銅以上）を初めて取った → ストアのレビュー依頼（1回だけ）
   */
  private afterResult(): void {
    const prompts = this.options.appPrompts;
    const result = this.lastResult;
    const before = this.medalAtStart;
    const after = this.currentMedal();
    this.medalAtStart = after;
    if (!prompts || !result || result.relaxed) return;

    if (this.context.daily && prompts.shouldAskNotice(this.dailyDaysPlayed)) {
      prompts.markNoticeAsked();
      window.setTimeout(() => void this.askDailyNotice(prompts), 2500);
      return;
    }
    // 結果をひと目見てもらってから（OS のレビュー画面が結果に重なるので少し待つ）
    const playCount = this.context.storage.playCount;
    // 配信モードのあいだは出さない（OS のレビュー画面が配信に映るため）
    window.setTimeout(
      () =>
        prompts.maybeRequestReview({
          before,
          after,
          playCount,
          streamerMode: this.context.settings.streamerMode,
        }),
      2500,
    );
  }

  private async askDailyNotice(prompts: AppPrompts): Promise<void> {
    const ok = await confirmDialog({
      title: '毎朝、今日の県を通知しますか？',
      body: '朝8時ごろに「今日の県は○○県です」とスマホに通知します。設定からいつでも止められます。',
      okLabel: '通知する',
      cancelLabel: '今はしない',
    });
    if (ok) await prompts.setNoticeEnabled(true);
  }

  /** 次のメダルまでの距離（この県の記録から）。 */
  private buildNextGoal(): string {
    const storage = this.context.storage;
    const goal = nextMedalGoal({
      maxLevel: this.context.maxLevel,
      reached: storage.maxLevelReached,
      reachedNoAssist: storage.bestLevelNoAssist,
    });
    return goal ? `<p class="result-goal">🎯 ${goal}</p>` : '';
  }

  private buildAssistNote(): string {
    const result = this.lastResult;
    if (!result?.assisted) return '';
    const reachedTop = result.maxLevelReached >= this.context.maxLevel;
    return reachedTop
      ? '<p class="result-note">コンティニュー・おはしを使ったので<b>金メダル</b>。使わずに届くと<b>白金メダル</b>です。</p>'
      : '<p class="result-note">このプレイはコンティニュー・おはしあり。</p>';
  }

  private buildTrophyHtml(list: readonly TrophyStatus[]): string {
    if (list.length === 0) return '';
    const escape = (text: string) =>
      text.replace(/[&<>"]/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;' })[c]!);
    // 結果画面が縦に長くなると「もう一度」が画面の外に出るので、1〜2行にまとめる（詳しくは棚で）
    const names = list
      .slice(0, 3)
      .map((s) => `${escape(s.trophy.name)}（${TIER_LABEL[s.trophy.tier]}）`)
      .join('・');
    const more = list.length > 3 ? ` ほか${list.length - 3}個` : '';
    return `<p class="result-trophies">🏆 新しいトロフィー ${list.length}個：${names}${more}</p>`;
  }

  private async continueByAd(): Promise<void> {
    if (this.busy) return;
    this.busy = true;
    try {
      const ok = await this.context.assist.requestContinue();
      if (ok) this.context.bus.emit('command:continue');
    } finally {
      this.busy = false;
    }
  }

  /**
   * 「はじめる」。県を移ってきたときは、ここで合間の広告の番を見る（2026-10-13）。
   * 県を巡る遊び方だと「もう一度」を押さないため、以前はここで一度も出なかった。
   */
  private async startGame(relaxed: boolean): Promise<void> {
    if (this.busy) return;
    this.busy = true;
    try {
      await this.context.assist.beforeStart();
    } finally {
      this.busy = false;
    }
    this.context.bus.emit('command:setMode', { relaxed });
    this.context.bus.emit('command:start');
  }

  private async playAgain(): Promise<void> {
    if (this.busy) return;
    this.busy = true;
    try {
      // 保存していない名場面があれば、消える前に一度だけ聞く（もう一度あそぶと新しいプレイで消えるため）
      const clip = this.options.getHighlight?.();
      if (clip && !clip.saved && this.askedHighlightClip !== clip) {
        this.askedHighlightClip = clip;
        const save = await confirmDialog({
          title: '名場面を保存しますか？',
          body: `「${highlightBanner(clip.level, clip.name)}」の動画は、もう一度あそぶと消えます。`,
          okLabel: '保存する',
          cancelLabel: '保存しない',
        });
        if (save) await shareHighlight(clip, this.highlightFileBase(clip)).catch(() => undefined);
      }
      // 数回に1回、合間の広告（アプリ版のみ・買い切りなら出ない）
      await this.context.assist.betweenGames();
    } finally {
      this.busy = false;
    }
    this.context.bus.emit('command:restart');
  }

  private highlightFileBase(clip: HighlightClip): string {
    return `gotochi-meshi-merge-${this.context.stage.regionId}-lv${clip.level}`;
  }

  /** 何で終わったか（どの料理が、どちら側からこぼれたか）。 */
  private buildSpillNote(): string {
    const spilled = this.lastResult?.spilled;
    if (!spilled) return '';
    const escape = (text: string) =>
      text.replace(/[&<>"]/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;' })[c]!);
    const side = spilled.side === 'left' ? '左' : '右';
    return `<p class="result-spill">💧 ${side}の縁から「${escape(spilled.name)}」（Lv${spilled.level}）がこぼれました</p>`;
  }

  private buildResultHtml(
    result: { score: number; highScore: number } | null,
    maxItemName: string,
    maxCombo = 0,
  ): string {
    const combo = maxCombo >= 2 ? ` ・ 最大コンボ <b>${maxCombo}</b>` : '';
    return `
      <div class="result-grid">
        <div>スコア<b>${(result?.score ?? 0).toLocaleString('ja-JP')}</b></div>
        <div>ハイスコア<b>${(result?.highScore ?? 0).toLocaleString('ja-JP')}</b>${highScoreMarkHtml(this.context.storage)}</div>
      </div>
      <p>最高到達: ${maxItemName}${combo}</p>
    `;
  }

  private showHelp(): void {
    const state = this.context.state;
    if (state.is(GameState.PLAYING)) {
      this.helpRequested = true;
      this.context.bus.emit('command:pause');
      // 止められなかったとき（落下の演出中など）に、次の一時停止で勝手に開かないように
      if (!state.is(GameState.PAUSED)) this.helpRequested = false;
    } else if (state.is(GameState.PAUSED)) {
      this.helpRequested = true;
      this.showPaused();
    } else if (state.is(GameState.READY)) {
      this.showReady();
      // 2回目からはたたんであるので、操作説明を押したときは開く
      this.bodyEl?.querySelector('details.rules-fold')?.setAttribute('open', '');
    }
    // 「操作説明」は盤面より下にあるので、押した位置のままだと説明の枠の上が画面の外に切れる
    if (this.root?.dataset.visible === 'true') this.cardEl?.scrollIntoView({ block: 'nearest' });
  }

  private show(options: {
    title: string;
    html: string;
    showShare?: boolean;
    /** 共有カードに載せられる名場面（結果画面だけ）。2枚以上あれば選べるようにする。 */
    moments?: Moment[];
    /** 開始画面・結果画面：読み上げでも後ろの画面に出ないよう、フォーカスを中に閉じ込める。 */
    modal?: boolean;
    actions: OverlayAction[];
  }): void {
    if (!this.root) return;
    this.root.setAttribute('aria-modal', String(options.modal === true));
    this.root.dataset.focusTrap = String(options.modal === true);
    if (this.titleEl) this.titleEl.textContent = options.title;
    if (this.bodyEl) this.bodyEl.innerHTML = options.html;

    if (this.shareEl) {
      this.shareEl.replaceChildren();
      this.shareable = null;
      if (options.showShare && this.lastResult) {
        const maxItem = this.context.itemByLevel.get(this.lastResult.maxLevelReached);
        const shareable: ShareableResult = {
          score: this.lastResult.score,
          highScore: this.lastResult.highScore,
          isNewRecord: this.lastResult.isNewRecord,
          maxLevelReached: this.lastResult.maxLevelReached,
          maxItemName: maxItem ? `レベル${maxItem.level} ${maxItem.name}` : 'なし',
          stageTitle: this.context.stage.title,
          dailyLabel: this.context.daily ? `今日の県 ${this.context.daily.shortDate}` : undefined,
          dailyDate: this.context.daily?.dateKey,
          regionName: this.context.stage.regionName,
          maxCombo: this.lastResult.maxCombo,
        };
        this.shareable = shareable;
        const visual = this.buildShareVisual();
        // 選んだ名場面をカードに載せる（ボタンは同じ visual を見ているので、選び直すと差し替わる）。
        // 共有ボタンを先に置き、場面えらびはその下に（既定の「最高Lvの瞬間」のままでもよいので、
        // 低い画面では押すほうを優先して見せる。SE で共有ボタンが下に隠れていた。2026-10-09）
        const picker =
          options.moments && options.moments.length > 0
            ? new MomentPicker(options.moments, (moment) => {
                visual.moment = { image: moment.image, caption: moment.label, kind: moment.kind };
              })
            : null;
        new ShareButtons(shareable, this.context.stage, visual).mount(this.shareEl);
        if (picker) {
          const more = this.shareEl.querySelector('.share-buttons__more');
          if (more) more.before(picker.element);
          else this.shareEl.append(picker.element);
        }
      }
    }

    if (this.actionsEl) {
      this.actionsEl.replaceChildren();
      const subRow = document.createElement('div');
      subRow.className = 'stage-overlay__sub';
      options.actions.forEach((action) => {
        const button = document.createElement('button');
        button.type = 'button';
        button.className = action.primary ? 'btn btn--primary' : action.sub ? 'btn btn--sub' : 'btn';
        button.textContent = action.label;
        button.setAttribute('aria-label', action.ariaLabel ?? action.label);
        button.addEventListener('click', action.onClick);
        (action.sub ? subRow : this.actionsEl!).append(button);
      });
      if (subRow.childElementCount > 0) this.actionsEl.append(subRow);
    }
    this.root.dataset.visible = 'true';
    if (this.cardEl) this.cardEl.scrollTop = 0;
    window.requestAnimationFrame(() => {
      this.actionsEl?.querySelector<HTMLButtonElement>('button')?.focus({ preventScroll: true });
      this.updateMoreHint();
    });
  }

  /**
   * 枠の下にまだ続きがあるかどうかを、カードの印（data-more）にする。
   * 「▼ 下につづきます」はカードの外（オーバーレイの下の余白）に出す。
   * カードの中に重ねると、高さ664pxで共有ボタンに重なっていた。
   */
  private updateMoreHint(): void {
    const card = this.cardEl;
    if (!card) return;
    const rest = card.scrollHeight - card.clientHeight - card.scrollTop;
    const more = rest > 8 ? 'true' : 'false';
    card.dataset.more = more;
    if (this.root && this.root.dataset.more !== more) {
      this.root.dataset.more = more;
      // 余白が変わるとカードの高さも変わるので、もう一度だけ測り直す
      window.requestAnimationFrame(() => {
        const again = card.scrollHeight - card.clientHeight - card.scrollTop > 8 ? 'true' : 'false';
        card.dataset.more = again;
        if (this.root) this.root.dataset.more = again;
      });
    }
  }

  /** 読み上げで1回だけ知らせる（画面には出さない）。 */
  private announce(text: string): void {
    this.announcer.textContent = '';
    window.setTimeout(() => {
      this.announcer.textContent = text;
    }, 100);
  }

  private hide(): void {
    if (!this.root) return;
    this.root.dataset.visible = 'false';
    this.root.dataset.focusTrap = 'false';
    this.root.setAttribute('aria-modal', 'false');
  }
}
